/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, ConfigProvider, Drawer, message } from 'antd'
import { CloseOutlined, WalletOutlined } from '@ant-design/icons'
import type { ThemeConfig } from 'antd'
import dayjs from 'dayjs'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { formatCurrency } from '@sportninja/common/currency'
import colors from '@sportninja/common/constants/appColors'
import { zIndex } from 'src/components/css'
import { useBalanceRosters } from '@sportninja/common/features/team-balance/hooks/useBalanceRosters'
import { useTeamBalance } from '@sportninja/common/features/team-balance/hooks/useTeamBalance'
import { useTeamBalanceMutations } from '@sportninja/common/features/team-balance/hooks/useTeamBalanceMutations'
import { useRosterPlayers } from '@sportninja/common/features/team-balance/hooks/useRosterPlayers'
import { teamStanding } from '@sportninja/common/features/team-balance/utils/teamStanding'
import type { RosterPlayerListItem } from '@sportninja/common/features/team/player/mapRosterPlayerToForm'
// @ts-ignore — RosterSelectNew is untyped JSX; this is the SAME roster switcher
// the Roster tab renders (src/pages/Team/UnifiedRoster/components/RosterHeader),
// reused verbatim so both tabs name and order rosters identically.
import RosterSelectNew from 'src/components/List/RosterSelectNew'
import RosterPlayerModal from 'src/features/team/player/RosterPlayerModal'
import type {
  BalanceBillingSchedule,
  BalanceLineKind,
  BalanceRegistration,
  OfflinePaymentBatch,
} from '@sportninja/common/services/teamBalance'
import LoadingSpinner from 'src/components/LoadingSpinner'
import BaseModal from 'src/components/ts-components/BaseModal'
import WarningModal from 'src/components/WarningModal'
import Text from 'src/components/ts-components/Text'
import EmptyStateBox from 'src/components/ts-components/EmptyStateBox'
import BalanceControls, { ALL_DIVISIONS } from './components/BalanceControls'
import BalanceEmptyState from './components/BalanceEmptyState'
import BalanceSummaryCards from './components/BalanceSummaryCards'
import BalanceHeaderPanel from './components/BalanceHeaderPanel'
import TeamStandingHeader from './components/TeamStandingHeader'
import PaymentMilestones from './components/PaymentMilestones'
import VerifyReviewModal from './components/VerifyReviewModal'
import type { BalanceSheetRow } from './types/balanceGrid'
import FirstRunModal from './components/FirstRunModal'
import AddPlayerSlotModal from './components/AddPlayerSlotModal'
import LockConfirmModal from './components/LockConfirmModal'
import AddInvoiceModal from './components/AddInvoiceModal'
import BatchInvoiceModal from './components/BatchInvoiceModal'
import OfflineHistorySection from './components/OfflineHistorySection'
import SlotExpandedRow from './components/SlotExpandedRow'
import OfflinePaymentModal from './components/OfflinePaymentModal'
import NotifyComposerModal from './components/NotifyComposerModal'
import type { NotifyComposerBody } from './components/NotifyComposerModal'
import TeamPaymentStatusBadge from './components/TeamPaymentStatusBadge'
import OfficialEffectList from './components/OfficialEffectList'
import { useOfficialLineFlows } from './hooks/useOfficialLineFlows'
import {
  dollarsToMinor,
  evenSplitReference,
  uniqueCurrencyCodes,
} from './utils/balanceMath'
import {
  previewLockBatch,
  previewVerifyOfflinePayment,
} from '@sportninja/common/services/teamBalance'
import type {
  BatchLockPreview,
  VerifyPreview,
} from '@sportninja/common/services/teamBalance'
import { isInvoiceable, isRemindable } from './utils/balanceGridActions'
import { finderHiddenCause, isSlotListedButHidden } from './utils/balanceFinder'
import type { FinderHiddenCause } from './utils/balanceFinder'
import { buildSheetRows } from './utils/buildSheetRows'
import {
  batchWriteKind,
  batchWriteKindOnSheet,
  candidateRowKey,
  hasNoPlayerLines,
  isOfficialLine,
  isRemindableSlot,
  isSlotTableRow,
  isViewerRestricted,
  lineLabel,
  lineName,
  writeKindFor,
} from './utils/balanceRowKind'
import { officialEffectCopy } from './utils/officialEffectCopy'
import {
  officialWriteError,
  officialWriteMessage,
} from './utils/officialWriteError'
import { cancelledToggleState } from './utils/cancelledToggleState'
import { guardMessage } from './utils/balanceError'
import { resolveSlotTarget } from './utils/resolveSlotTarget'
import { headerMoney } from './utils/balanceTax'
import { balanceLabel, balanceText } from './utils/balanceI18n'
import { formatStandingDay } from './utils/balanceCutoff'
import type { StandingFormatters } from './utils/standingCopy'

/**
 * ag-grid enters the bundle exclusively through this import. `pages/Team` pulls
 * BalanceTab in eagerly, so a static import here would put the whole grid in the
 * main chunk for every user who never opens the tab.
 */
const BalanceGrid = lazy(() => import('./components/BalanceGrid'))

/**
 * The tenant BODY stack, as a string. antd component tokens take a plain
 * font-family declaration, so `font.body` (an emotion object) cannot be handed
 * to one — this is the single place the stack is spelled out for antd, and it
 * mirrors `src/components/css.js`'s sportninjaFont/canlanFont bodies.
 */
/**
 * The app's fixed nav. Anything at or above this belongs to the shell rather
 * than to the page, and is where the payments Drawer's content offset stops
 * looking for its anchor.
 */
const APP_HEADER_HEIGHT = 60

const statusBadgeListStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`

/** The same gap below the scope row as Billing's empty box. */
const seasonEmptyStyle = css`
  margin-top: 24px;
`

/**
 * The picker's home when the Registration tab's Season picker does not control
 * the roster (the admin Registration Details modal mounts this tab directly).
 * Above the panel, the same altitude the Registration tab's scope row gives it.
 */
const inlineRosterStyle = css`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`

const BODY_FONT_FAMILY = !isCanlan
  ? 'Inter, Arial, Helvetica, sans-serif'
  : 'Gilroy, Arial, Helvetica, sans-serif'

/**
 * Scoped tenant-font correction for this tab's antd surfaces.
 *
 * The app-wide theme (`src/configs/index.tsx`) sets a tenant `fontFamily` on only
 * SIX component tokens — Button, Input, Select, Alert, Message, Menu — and never
 * on the global `token`. Every antd component outside that list falls through to
 * antd's SEED fontFamily, which is the OS system stack (`-apple-system` → SF Pro
 * on macOS). Measured on this tab before this fix: the Modal title, its body copy
 * and its InputNumber were all system font — not Inter, not Gilroy. No Canlan
 * face is even loaded, so it is not a tenant mix-up; it is simply neither tenant.
 * (SF Pro and Gilroy are both geometric sans, which is why it reads as one.)
 *
 * Note InputNumber is a SEPARATE antd token from Input, which is why the app-wide
 * Input override never reached the amount and slot-count fields.
 *
 * The Button entry stays. The app-wide theme forces the CONDENSED HEADING face on
 * every antd Button, but the house PRIMARY ACTION button is not an antd Button at
 * all — it is `HeaderActionButton`, which renders `font.body` (Inter / Gilroy) at
 * weight 400. That is what "Add Player Slot" on this tab and the Registration
 * Setup button are, so the body face is the one that matches the house style, and
 * antd's condensed default is the outlier. The value MUST carry `!important` —
 * the outer token uses it, and `!important` wins on the cascade regardless of
 * specificity.
 *
 * The real fix for the rest is one line — `token.fontFamily` at the theme root,
 * which every per-component token inherits from. That restyles text on every
 * screen in the app in both tenants, so it is deliberately NOT done here; it is
 * written up in TENANT-FONT-GLOBAL-TOKEN-FINDING.md. This covers the components
 * the Balance tab actually renders, verified against its antd imports, minus the
 * ones already correct app-wide (Input/TextArea, Select, Alert, message), and
 * should be DELETED when the global token lands.
 *
 * antd merges a nested theme's component tokens INTO the parent's per key, so the
 * rest of each token — Input's backgrounds, Modal's paddings — is inherited
 * untouched. Context flows through portals, so modal footers, Popconfirm and
 * Tooltip are covered even though antd renders them into `document.body`.
 */
const BALANCE_THEME: ThemeConfig = {
  components: {
    Button: { fontFamily: `${BODY_FONT_FAMILY} !important` },
    Modal: { fontFamily: BODY_FONT_FAMILY },
    // Distinct from Input: the amount + slot-count fields are InputNumber.
    InputNumber: { fontFamily: BODY_FONT_FAMILY },
    Table: { fontFamily: BODY_FONT_FAMILY },
    Tooltip: { fontFamily: BODY_FONT_FAMILY },
    Popconfirm: { fontFamily: BODY_FONT_FAMILY },
    Tag: { fontFamily: BODY_FONT_FAMILY },
    Dropdown: { fontFamily: BODY_FONT_FAMILY },
    /**
     * U2 — the drawer title ("Payments for <player>") had the same system-font
     * problem, and antd additionally paints drawer titles at 85% white, so the
     * one element that says WHOSE payments these are read dimmer AND in a
     * different face than the table header beneath it.
     */
    Drawer: {
      fontFamily: BODY_FONT_FAMILY,
      /**
       * `colorText`, NOT `colorTextHeading` — antd's drawer style reads `colorText`
       * for `.ant-drawer-title` (drawer/style/index.js:185), so the heading token
       * silently does nothing here. It is the ONLY thing that rule colours, so
       * this does not bleed into the drawer body.
       */
      colorText: colors.NEUTRAL_0,
    },
  },
}

type BalanceTabProps = {
  teamId: string
  /**
   * D40 — the roster the page is already showing, if any. Treated as a
   * preference and validated against the caller's visible rosters before it is
   * adopted (A-4), so it can never force a 404.
   */
  preferredRosterId?: string | null
  /**
   * Keys the per-caller roster-discovery cache. Passed from the Team page so the
   * tab and the page's gating probe share ONE cache entry (and one fetch).
   */
  authUserId?: string | number | null
  /**
   * SN-4709 follow-up — the feed `post` ability, threaded from the Team page. Hides
   * "Post & Notify Team" when false; the server stays authoritative regardless.
   */
  canPost?: boolean
  /**
   * SN-4709 follow-up (Command Center) — team sport, for the Edit Player editor's
   * position options. Threaded from the Team page (team-level, always loaded).
   */
  sportId?: string | null
  sportName?: string
  /**
   * Whether the caller may edit roster players. Gates the kebab's Edit Player
   * action AND the roster-players fetch it needs — off ⇒ neither happens. The
   * server stays authoritative on the Save regardless.
   */
  canManageRoster?: boolean
  /**
   * The roster the Registration tab's shared Season picker selected. Present
   * (even null) makes the roster CONTROLLED: this tab renders no roster picker,
   * shows that roster's sheet when discovery lists it, and otherwise says the
   * season has no Team Budget — it never falls back to another roster's money.
   *
   * Absent — the admin modal, which mounts this tab directly — the tab owns the
   * selection and renders its own picker above the panel.
   */
  rosterId?: string | null
  /**
   * SN-4709 (Dennis/Jeff, Sep 1) — PINNED TO ONE SHEET.
   *
   * The admin Registration Details modal renders this tab for the ONE team
   * registration it is already showing, so both selectors have to be pinned, not
   * merely defaulted: initial state is All Divisions and a roster change resets
   * it back there, so a `preferredRosterId`-style hint would let the admin walk
   * out of the registration they opened and into another team's money without
   * anything on screen saying so.
   *
   * When set, the roster switcher and the division filter are HIDDEN and both
   * values are fixed. Discovery still decides whether the roster is visible at
   * all — a scope the caller may not see resolves to a notice, never to some
   * other roster's sheet.
   *
   * Team registrations only. A PLAYER registration in a distribution division
   * has real lock-generated billing rows of its own, and pinning this over them
   * would hide the data the tab is supposed to replace.
   */
  scope?: { rosterId: string; registrationId: string } | null
  /**
   * Opens the Registration tab's Player Finder sub-tab on this team registration.
   * Absent where there is no such sub-tab (the admin Registration Details modal),
   * which hides the sheet's link to it.
   */
  onOpenPlayerFinder?: (teamRegistrationId: string) => void
  /**
   * SN-8449 — the z-index of the overlay this sheet is mounted INSIDE: the admin
   * Registration Details modal (10001). Absent on the Team page.
   *
   * antd popups inside the sheet follow the host by themselves (the host modal
   * raises antd's popup base to its layer). What they cannot follow is an overlay
   * that carries its OWN z-index — the payments drawer (`zIndex.overlay`, 1030)
   * and the Edit Player editor (15) — both of which opened invisibly behind the
   * host. Those clear this value instead.
   */
  hostZIndex?: number
}

/**
 * SN-4709 Balance tab root (tenant-neutral, D32). Owns the ROSTER context
 * (D40) plus the division filter, the row filter and cancelled-visibility state;
 * everything money-related comes from the server payload — mutations invalidate
 * and refetch (D20: the server owns re-splits, KPIs and statuses).
 *
 * Player-name filtering lives in the grid's own Player column filter — there is
 * no separate search input.
 */
/** TF-03 — the sheet's notice for listed slots whose ad is hidden, by cause. */
const FINDER_HIDDEN_NOTICE_KEYS: Record<FinderHiddenCause, string> = {
  finder_disabled: 'common:balanceFinderHiddenFinderDisabled',
  registration_private: 'common:balanceFinderHiddenPrivate',
  recruiting_off: 'common:balanceFinderRecruitingOff',
  roster_full: 'common:balanceFinderHiddenRosterFull',
  other: 'common:balanceFinderHidden',
}

/** Whether roster discovery lists this roster for the caller. */
const isDiscoveredRoster = (
  rosters: readonly { id: string }[],
  id: string | null | undefined
): id is string => Boolean(id) && rosters.some((roster) => roster.id === id)

const BalanceTab = ({
  teamId,
  preferredRosterId = null,
  authUserId = null,
  canPost = false,
  sportId = null,
  sportName,
  canManageRoster = false,
  rosterId,
  scope = null,
  onOpenPlayerFinder,
  hostZIndex,
}: BalanceTabProps) => {
  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string>(
    scope?.registrationId ?? ALL_DIVISIONS
  )
  const [rowFilter, setRowFilter] = useState<'all' | 'owes' | 'paid' | 'open'>(
    'all'
  )
  // D39 — verification ALWAYS goes through re-review, so a drifted batch can
  // never be settled by a click that predates the drift.
  const [verifyBatchId, setVerifyBatchId] = useState<string | null>(null)
  // Sep 23 — the batch's own `line_kind`, sent back on the preview, the verify and
  // every retry: an official batch without it is refused (409 refresh_required).
  const [verifyKind, setVerifyKind] = useState<BalanceLineKind | undefined>(
    undefined
  )
  const [verifyPreview, setVerifyPreview] = useState<VerifyPreview | null>(null)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  // D39 deep link: /team/{id}/balance?roster=&verify= from the actionable
  // notification. The roster is adopted only if DISCOVERY says the caller may
  // see it, and the batch is re-resolved server-side, so a stale or unauthorised
  // link degrades to a normal sheet rather than leaking anything.
  const [deepLink] = useState(() => {
    if (typeof window === 'undefined') {
      return { roster: null as string | null, verify: null as string | null }
    }
    const params = new URLSearchParams(window.location.search)
    return { roster: params.get('roster'), verify: params.get('verify') }
  })

  const [showCancelled, setShowCancelled] = useState(false)
  const [firstRunDismissed, setFirstRunDismissed] = useState(false)
  // C-5 — the payments Drawer target (replaces inline row expansion).
  const [paymentsSlotId, setPaymentsSlotId] = useState<string | null>(null)
  /**
   * SN-4709 (Inon, Aug 12) — "align 'Payments for …' on that red line": the
   * payments Drawer starts where THIS TAB's content starts, rather than at the
   * top of the window with its title jammed against the browser chrome.
   *
   * Measured off our own root when the drawer opens, because the page chrome
   * above it is not a constant — the team hero scales with viewport width, so
   * the content top is 385px at 1440 and 268px at 1600. A hardcoded offset is
   * wrong at every width but one.
   *
   * Measuring ONCE at open is safe: `useModalScroll` freezes the page for the
   * drawer's whole lifetime, so the value cannot go stale underneath it.
   */
  const contentRef = useRef<HTMLDivElement>(null)
  const [drawerTop, setDrawerTop] = useState(0)
  const [lockTargetSlotId, setLockTargetSlotId] = useState<string | null>(null)
  const [addInvoiceSlotId, setAddInvoiceSlotId] = useState<string | null>(null)
  /** SN-8310 — the added invoice the drawer's Remove is asking about. */
  const [removeInvoiceTarget, setRemoveInvoiceTarget] = useState<{
    slotId: string
    bill: BalanceBillingSchedule
    currencyCode: string
  } | null>(null)
  const [offlineTargetSlotId, setOfflineTargetSlotId] = useState<string | null>(
    null
  )
  /**
   * SN-4709 × SN-8081 (D46). Two independent openers for ONE modal:
   * `addSlotOpen` is Add Player Slot (create mode); `finderTarget` is a row
   * kebab's Player Finder action on one slot — "List in Player Finder…" (list)
   * or "Edit Player Finder Position" (edit-position) — and names the slot the
   * position will be written to.
   */
  const [addSlotOpen, setAddSlotOpen] = useState(false)
  const [finderTarget, setFinderTarget] = useState<{
    slotId: string
    mode: 'list' | 'edit-position'
  } | null>(null)
  /**
   * SN-4709 (James, Aug 12) — the ONE notify composer's target. Null is closed;
   * `{ slotId }` is a row's Remind button; `{ slotId: null }` is the toolbar's
   * Notify, where `selected` narrows the audience to the grid's ticked rows.
   *
   * Same shape the three separate modals shared between them — merging the
   * SHELL did not change what a notification is aimed at.
   */
  const [notifyTarget, setNotifyTarget] = useState<{
    slotId: string | null
    selected?: boolean
  } | null>(null)
  /**
   * SN-4709 (James, Jul 31) — the sheet's row selection, lifted here so the
   * toolbar's "Send N Invoices" and Notify's "Remind Selected" act on the SAME
   * ticked rows. AG Grid owns the selection; this mirrors it.
   */
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([])
  const [batchInvoiceOpen, setBatchInvoiceOpen] = useState(false)
  const [batchPreview, setBatchPreview] = useState<BatchLockPreview | null>(
    null
  )
  const [batchLoading, setBatchLoading] = useState(false)
  const [batchError, setBatchError] = useState<string | null>(null)
  const [splitEvenlyOpen, setSplitEvenlyOpen] = useState(false)

  // D40 — discovery drives the roster switcher; the sheet below is scoped to the
  // selected roster. Same react-query key as the Team page's tab probe (both
  // pass the same teamId + authUserId), so the switcher costs no extra fetch.
  // D40 — DISCOVERY is authoritative for which rosters exist here and which one
  // we default to; RosterSelectNew supplies only the presentation. The generic
  // /teams/{id}/rosters endpoint is separately `view`-gated and lists every
  // roster of the team, so driving the switcher from it broke both directions: a
  // data-linked rep who passes manageBalance but not `view` got NO roster, while
  // a view-authorised user was offered rosters with no balance sheet behind them.
  const {
    rosters,
    isLoading: isLoadingRosters,
    refetch: refetchRosters,
  } = useBalanceRosters({ teamId, authUserId })

  const isSeasonControlled = rosterId !== undefined
  const [pickedRosterId, setPickedRosterId] = useState<string | null>(null)
  // Controlled: derived, never adopted into state, so a season with no sheet
  // cannot render (or fetch) the previously picked roster for even one frame.
  const selectedRosterId = isSeasonControlled
    ? isDiscoveredRoster(rosters, rosterId)
      ? rosterId
      : null
    : pickedRosterId

  // Validate the selection against discovery on every change: a stored or
  // preferred roster the caller cannot actually see must never be adopted (it
  // would 404 every subsequent call), and a roster that disappears — permissions
  // changed, cost distribution switched off — must fall back rather than strand.
  useEffect(() => {
    // The Registration tab's Season picker owns a controlled roster.
    if (isSeasonControlled) {
      return
    }
    if (rosters.length === 0) {
      if (pickedRosterId !== null) {
        setPickedRosterId(null)
      }
      return
    }
    const visible = (id: string | null | undefined) =>
      Boolean(id) && rosters.some((roster) => roster.id === id)

    // A PINNED scope is not a preference: it either resolves to its own roster
    // or to nothing. Falling back to rosters[0] here would silently show the
    // admin a different roster's money under the heading of the registration
    // they opened.
    if (scope) {
      const pinned = visible(scope.rosterId) ? scope.rosterId : null
      if (pickedRosterId !== pinned) {
        setPickedRosterId(pinned)
      }
      return
    }

    if (visible(pickedRosterId)) {
      return
    }
    setPickedRosterId(
      visible(deepLink.roster)
        ? (deepLink.roster as string)
        : visible(preferredRosterId)
        ? (preferredRosterId as string)
        : rosters[0].id
    )
  }, [
    isSeasonControlled,
    rosters,
    preferredRosterId,
    pickedRosterId,
    deepLink.roster,
    scope,
  ])

  // A-4 — a roster switch is a full context change: every filter, dismissal,
  // selection and open modal belongs to the roster it was made on. Carrying any
  // of it across would silently apply one roster's view (or worse, a pending
  // money action targeting one roster's slot) to another.
  useEffect(() => {
    setSelectedRegistrationId(scope?.registrationId ?? ALL_DIVISIONS)
    setRowFilter('all')
    setShowCancelled(false)
    setFirstRunDismissed(false)
    setPaymentsSlotId(null)
    setVerifyBatchId(null)
    setVerifyPreview(null)
    setVerifyError(null)
    setLockTargetSlotId(null)
    setOfflineTargetSlotId(null)
    // A payment reminder / team email / feed post belongs to the roster it was
    // opened on — a roster switch closes the composer so nothing is sent against
    // the wrong one.
    setNotifyTarget(null)
  }, [selectedRosterId, scope?.registrationId])

  useEffect(() => {
    if (paymentsSlotId === null) {
      return
    }
    /*
       Where the TEAM's tab strip begins — the line Inon marked, and the top of
       everything that belongs to this page rather than to the app shell.

       Found by a RULE rather than a selector: walk up from our own root and take
       the outermost ancestor that still starts below the app's fixed header. The
       team hero above it scales with viewport width (the strip sits at 268px on
       a 1600 window and 385px on a 1440 one), so no constant is right, and an
       emotion-hashed class name would be a selector that compiles fine and
       silently stops matching. Anything that fails to qualify simply leaves the
       drawer where it already was.
    */
    let anchor: HTMLElement | null = contentRef.current
    for (
      let node = contentRef.current?.parentElement ?? null;
      node && node !== document.body;
      node = node.parentElement
    ) {
      const nodeTop = node.getBoundingClientRect().top
      if (nodeTop <= APP_HEADER_HEIGHT) {
        break
      }
      anchor = node
    }
    const top = anchor?.getBoundingClientRect().top ?? 0
    // Clamped: a drawer that starts past half the window is a letterbox, and a
    // negative top (content scrolled above the fold) would lift it off screen.
    setDrawerTop(Math.max(0, Math.min(top, window.innerHeight * 0.5)))
  }, [paymentsSlotId])

  const { registrations, isLoading, isError, isFetching, refetch } =
    useTeamBalance({ teamId, rosterId: selectedRosterId })
  const mutations = useTeamBalanceMutations(teamId, selectedRosterId ?? '')

  /**
   * Sep 23 — every change to a team official's line (opening it from a $0 candidate,
   * a new amount, undoing an added invoice) is previewed and, when it moves player
   * shares, confirmed with its effect listed. The round trip lives in the hook.
   */
  const officialFlows = useOfficialLineFlows({
    teamId,
    rosterId: selectedRosterId ?? '',
    mutations,
    refetch,
  })

  /**
   * The `expected_kind` a write on this slot sends — `official` for a team official's
   * line, nothing for a player's share (R2-4). Read from the LIVE payload.
   */
  const slotWriteKind = (slotId: string): BalanceLineKind | undefined => {
    const target = resolveSlotTarget(registrations, slotId)

    return target ? writeKindFor(target.slot) : undefined
  }

  /**
   * A refused write on a team official's line explains itself in an official's terms
   * (out of date, own line); a player's keeps the server's message as before.
   */
  const writeFailureMessage = (slotId: string, error: unknown): string =>
    slotWriteKind(slotId) === 'official'
      ? officialWriteMessage(error)
      : guardMessage(error)

  // SN-4709 follow-up — the roster Edit Player editor for a slot's occupant. The
  // roster-players endpoint is `view`-gated (independent of manageBalance), so it
  // is fetched ONLY when the caller can manage the roster, and a 403/failure
  // degrades to "no Edit Player action" (retry: false) rather than an error. The
  // row it returns is the exact shape the editor prefills from — no second fetch.
  const [editPlayerId, setEditPlayerId] = useState<string | null>(null)
  const rosterPlayersQuery = useRosterPlayers({
    teamId,
    rosterId: selectedRosterId,
    enabled: Boolean(canManageRoster),
  })
  const rosterPlayersById = useMemo(() => {
    const map = new Map<string, RosterPlayerListItem>()
    for (const player of rosterPlayersQuery.data ?? []) {
      if (player?.id) {
        map.set(String(player.id), player)
      }
    }
    return map
  }, [rosterPlayersQuery.data])

  // Resolve the occupant's player id from the LIVE payload, then open the editor if
  // that player is on the roster. A registered-but-not-rostered occupant (Phase 2's
  // null-position case) has nothing to edit here, so it says so instead of opening
  // a blank editor that a Save could file against the wrong/empty record.
  const openPlayerEditor = (slotId: string) => {
    for (const registration of registrations) {
      const slot = registration.slots.find((s) => s.id === slotId)
      if (slot?.player?.id) {
        if (rosterPlayersById.has(slot.player.id)) {
          setEditPlayerId(slot.player.id)
        } else {
          message.error(balanceText('common:balanceEditPlayerUnavailable'))
        }
        return
      }
    }
  }
  const editPlayerRow =
    editPlayerId !== null ? rosterPlayersById.get(editPlayerId) ?? null : null

  /*
   * SN-8357 — roster membership for the ⋮'s Edit Player gate.
   *
   * `openPlayerEditor` below already checks exactly this before opening, but it only
   * runs ON CLICK, so an occupant who is registered but not yet on the roster was
   * offered the action and then told it could not be done. Handing the grid the same
   * set lets the menu answer before the click.
   *
   * UNDEFINED until the query succeeds, deliberately: an empty set would mean "nobody
   * is on the roster", which is a different statement from "we do not know yet".
   */
  const rosterPlayerIds = useMemo(
    () =>
      rosterPlayersQuery.isSuccess
        ? new Set(rosterPlayersById.keys())
        : undefined,
    [rosterPlayersQuery.isSuccess, rosterPlayersById]
  )

  // A roster switch closes the editor — its occupant belongs to the roster it was
  // opened on (mirrors the A-4 reset below for every other per-roster selection).
  useEffect(() => {
    setEditPlayerId(null)
  }, [selectedRosterId])

  const runSlotMutation = (
    mutation: { mutateAsync: (vars: { slotId: string }) => Promise<unknown> },
    slotId: string
  ) => {
    mutation
      .mutateAsync({ slotId })
      .catch((error) => message.error(guardMessage(error)))
  }

  /**
   * The amount an in-flight PUT is carrying, so the cell can keep showing what
   * was typed instead of snapping back to the server's previous figure for the
   * duration of the request. Display only — never written to the query cache,
   * and deliberately NOT fed into the totals, which stay server-derived (D20).
   * If the PUT is rejected this simply empties and the server value returns.
   */
  const pendingAmounts: Record<string, number> = useMemo(() => {
    const amounts: Record<string, number> = {}
    const variables = mutations.updateSlotAmount.variables as
      | { slotId?: string; amount?: number }
      | undefined
    if (
      mutations.updateSlotAmount.isPending &&
      variables?.slotId &&
      typeof variables.amount === 'number'
    ) {
      amounts[variables.slotId] = variables.amount
    }
    // Sep 23 — a candidate being turned into a line keeps showing what was typed
    // until the refetch brings the real line.
    const created = mutations.createOfficialLine.variables
    if (mutations.createOfficialLine.isPending && created) {
      amounts[candidateRowKey(created.registrationId, created.payerUserId)] =
        created.amount
    }
    return amounts
  }, [
    mutations.updateSlotAmount.isPending,
    mutations.updateSlotAmount.variables,
    mutations.createOfficialLine.isPending,
    mutations.createOfficialLine.variables,
  ])

  const visibleRegistrations: BalanceRegistration[] = useMemo(() => {
    if (selectedRegistrationId === ALL_DIVISIONS) {
      return registrations
    }
    return registrations.filter(
      (registration) => registration.id === selectedRegistrationId
    )
  }, [registrations, selectedRegistrationId])

  // Per-registration affordances (header chips, threshold bar, Add Line Item)
  // only make sense against ONE sheet — hidden on a mixed All-Divisions view.
  const singleRegistration =
    visibleRegistrations.length === 1 ? visibleRegistrations[0] : null

  const currencyCodes = uniqueCurrencyCodes(visibleRegistrations)
  const showMoneyKpis = currencyCodes.length === 1

  /**
   * SN-4709 (James, Aug 12) — the EVEN SPLIT column's replacement, resolved ONCE
   * here and handed to both the things that state it: the toolbar line beside
   * Split Evenly (formatted) and the PLAYER SHARE header tooltip (raw). Two
   * statements of one fact, from one derivation, so they cannot disagree.
   *
   * Single-registration only, by construction — `evenSplitReference` takes the
   * one registration or null, and the All-Divisions view has no single "per
   * player" figure to state.
   */
  const evenSplit = useMemo(() => {
    const amount = evenSplitReference(singleRegistration)
    if (amount === null) {
      return null
    }

    return { amount, currency: singleRegistration?.currency?.code ?? 'CAD' }
  }, [singleRegistration])

  /**
   * SN-8310 (Sep 22) — ONE instant for the whole header, so the banner, the
   * countdown and the Today flag cannot straddle a midnight between them.
   */
  const now = dayjs()

  /**
   * SN-8310 — where the ONE registration on screen stands, from the server's
   * status. Null on All Divisions (divisions have different dates, so there is
   * no single timeline) and when the server sends no status (D10: no minimum, or
   * dates it cannot resolve) — the header then states the money alone.
   */
  const standing = singleRegistration
    ? teamStanding(singleRegistration.payment_status, now)
    : null

  /**
   * SN-4709 (D6) — one badge per DIVISION registration, never aggregated, each
   * naming its division, because "Not Qualified" with no division attached is
   * unreadable. All Divisions only (Sep 22 pick: pills plus summed money); a
   * single registration carries its pill in the standing banner.
   */
  const statusBadges = (
    <span css={statusBadgeListStyle} data-testid='team-payment-status-list'>
      {visibleRegistrations.map((registration) => (
        <TeamPaymentStatusBadge
          key={registration.id}
          status={registration.payment_status}
          divisionName={registration.division?.name}
        />
      ))}
    </span>
  )

  /**
   * SN-4709 (Inon, Sep 2) — the roster picker, rendered into the Registration
   * tab's scope row rather than into this tab's header panel.
   *
   * Three shapes, in priority order:
   *
   *  - PINNED SCOPE (the admin Registration Details modal): the roster is a fact
   *    about the registration the admin opened, not a choice. It names the sheet.
   *  - ONE ROSTER: a dropdown holding a single option is chrome describing a
   *    decision nobody is making, so the name renders as text. Same rule the
   *    pinned scope already followed.
   *  - OTHERWISE: the compact switcher.
   */
  const rosterPickerNode =
    scope || rosters.length <= 1 ? (
      <Text variant='paragraphSmall' weight='semiBold' color={colors.NEUTRAL_0}>
        {scope
          ? singleRegistration?.division.root_name
            ? `${singleRegistration.division.name} · ${singleRegistration.division.root_name}`
            : singleRegistration?.division.name ?? ''
          : rosters[0]?.schedule_name ?? rosters[0]?.name ?? ''}
      </Text>
    ) : (
      <RosterSelectNew
        compact
        teamId={teamId}
        teamName={''}
        permission={{}}
        rosters={rosters}
        loadRosters={refetchRosters}
        selectedRosterId={selectedRosterId}
        setSelectedRosterId={setPickedRosterId}
      />
    )

  /*
   * Rendered in place above the panel — unless the Registration tab's Season
   * picker controls the roster, in which case that picker is the scope control.
   */
  const rosterPicker = isSeasonControlled ? null : (
    <div css={inlineRosterStyle}>{rosterPickerNode}</div>
  )

  const evenSplitLabel = useMemo(
    () =>
      evenSplit === null
        ? null
        : formatCurrency({
            amount: dollarsToMinor(evenSplit.amount),
            currencyCode: evenSplit.currency,
          }),
    [evenSplit]
  )

  /*
   * The sheet's rows in display order — slot rows (players' shares and, Sep 23, team
   * officials' lines) and the officials' $0 candidate rows. See `buildSheetRows`.
   */
  const rows: BalanceSheetRow[] = useMemo(
    () =>
      buildSheetRows({
        registrations: visibleRegistrations,
        showCancelled,
        rowFilter,
      }),
    [visibleRegistrations, showCancelled, rowFilter]
  )

  /*
   * SN-8346 — Display Cancelled reads as dead UI whenever flipping it has
   * nothing to reveal. Computed from the SAME `visibleRegistrations` the `rows`
   * memo reads, so the toggle and the sheet can never disagree about what is
   * eligible.
   */
  const cancelledToggle = useMemo(
    () =>
      cancelledToggleState({
        slots: visibleRegistrations.flatMap(
          (registration) => registration.slots
        ),
        rowFilter,
        showCancelled,
      }),
    [visibleRegistrations, rowFilter, showCancelled]
  )

  // Resolve the lock target (slot + its full parent registration for the modal
  // preview) from the server payload, never the flattened table row.
  const lockTarget = useMemo(
    () => resolveSlotTarget(registrations, lockTargetSlotId),
    [lockTargetSlotId, registrations]
  )

  // C-5 — resolve the Drawer's slot from the SERVER payload, so its contents
  // refresh with the sheet after a void/verify rather than showing a stale copy.
  const paymentsTarget = useMemo(
    () => resolveSlotTarget(registrations, paymentsSlotId),
    [paymentsSlotId, registrations]
  )

  // The kebab's finder target resolved from the SERVER payload: its registration
  // decides the recruiting hint, and its current position seeds Edit Position.
  const finderTargetSlot = useMemo(
    () => resolveSlotTarget(registrations, finderTarget?.slotId ?? null),
    [finderTarget, registrations]
  )

  // SN-8310 — Add Invoice's slot, from the server payload like the others: the
  // modal shows the share the SERVER holds now, not the one the rep clicked on.
  const addInvoiceTarget = resolveSlotTarget(registrations, addInvoiceSlotId)

  /**
   * Registrations with listed slots whose ad players cannot see. Those slots read
   * "Ad Hidden" in the grid; this is the one place that says why — the team's
   * recruiting switch, the competition turning Player Finder off, a full roster —
   * so the sheet never implies a live ad, nor blames the switch for a hide the
   * rep cannot change there (TF-03).
   */
  const hiddenFinderListings = useMemo(
    () =>
      visibleRegistrations
        .map((registration) => ({
          registration,
          count: registration.slots.filter(isSlotListedButHidden).length,
          cause: finderHiddenCause(registration),
        }))
        .filter(({ count }) => count > 0),
    [visibleRegistrations]
  )

  const offlineTarget = useMemo(
    () => resolveSlotTarget(registrations, offlineTargetSlotId),
    [offlineTargetSlotId, registrations]
  )

  const verifyingBatchId = mutations.verifyOffline.isPending
    ? mutations.verifyOffline.variables?.batchId ?? null
    : null

  /**
   * Slots with a write in flight. D20 keeps every number server-owned, so a row
   * shows its OLD money until the refetch lands — without a working state an
   * amount edit looks like it did nothing for the whole round trip.
   */
  const pendingSlotIds: string[] = useMemo(() => {
    const inFlight = [
      mutations.updateSlotAmount,
      mutations.lockSlot,
      mutations.unlockSlot,
      mutations.releaseSlot,
      mutations.removeSlot,
      mutations.recordOffline,
      // D46 — without this the row shows no working state for the whole finder
      // round trip and the tick looks like it did nothing.
      mutations.updateSlotFinderListing,
      // SN-8310 — both move the share, so the row shows its working state too.
      mutations.addInvoice,
      mutations.removeAddedInvoice,
    ]
      .filter((mutation) => mutation.isPending)
      .map(
        (mutation) =>
          (mutation.variables as { slotId?: string } | undefined)?.slotId
      )
      .filter((slotId): slotId is string => Boolean(slotId))

    // Sep 23 — a candidate's line being opened: its row shows the working state.
    const created = mutations.createOfficialLine.variables
    if (mutations.createOfficialLine.isPending && created) {
      inFlight.push(
        candidateRowKey(created.registrationId, created.payerUserId)
      )
    }

    return Array.from(new Set(inFlight))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mutations.createOfficialLine.isPending,
    mutations.createOfficialLine.variables,
    mutations.updateSlotAmount.isPending,
    mutations.updateSlotAmount.variables,
    mutations.lockSlot.isPending,
    mutations.lockSlot.variables,
    mutations.unlockSlot.isPending,
    mutations.unlockSlot.variables,
    mutations.releaseSlot.isPending,
    mutations.releaseSlot.variables,
    mutations.removeSlot.isPending,
    mutations.removeSlot.variables,
    mutations.recordOffline.isPending,
    mutations.recordOffline.variables,
    mutations.updateSlotFinderListing.isPending,
    mutations.updateSlotFinderListing.variables,
    mutations.addInvoice.isPending,
    mutations.addInvoice.variables,
    mutations.removeAddedInvoice.isPending,
    mutations.removeAddedInvoice.variables,
  ])

  /**
   * SN-8310 — the header's money is the SERVER's, for the registrations on
   * screen: never summed from `rows`, which the Show filter and Display Cancelled
   * narrow. Summing the rows let a screen filter change what the team owes (and
   * the qualifying shortfall with it), emptied the figures when a filter matched
   * nothing, and missed money paid by released players. The grid's total row
   * still follows the filters — that one IS a statement about what is on screen.
   */
  const money = useMemo(
    () => headerMoney(visibleRegistrations),
    [visibleRegistrations]
  )

  /**
   * The collection card's DENOMINATOR and its allocation differential (James,
   * Jul 31). Both come from the REGISTRATIONS, not from the rendered rows: the
   * fee is what the admin configured for the division, and `allocated` /
   * `unallocated` are server KPIs (the balance ledger) so the card can never
   * disagree with the sheet by a rounding penny. Summed across the visible
   * registrations for the All-Divisions view — money is suppressed there anyway
   * unless they share a currency.
   *
   * The amount due and its tax (cash INCLUDING tax) come from `headerMoney`.
   */
  const feeTotals = useMemo(() => {
    const minor = (v: number) => Math.round(v * 100)

    return visibleRegistrations.reduce(
      (acc, registration) => ({
        teamCost: acc.teamCost + minor(Number(registration.team_cost) || 0),
        allocated:
          acc.allocated + minor(Number(registration.kpis?.allocated) || 0),
        unallocated:
          acc.unallocated + minor(Number(registration.kpis?.unallocated) || 0),
      }),
      { teamCost: 0, allocated: 0, unallocated: 0 }
    )
  }, [visibleRegistrations])

  // SN-4709 follow-up — the "currently N" reminder hint. Counted over the WHOLE
  // roster payload (every registration), NOT the screen-filtered `rows`: the bulk
  // reminder is server-authoritative and ignores the grid filters (decision 10), so
  // the disabled state and the confirm copy must reflect the unfiltered set. The
  // server recomputes the exact list on send.
  /**
   * The money figures. One object, from `money`, so the banner's sentence, the
   * milestones and the figures under them read the SAME numbers.
   */
  const summaryProps = {
    teamCost: feeTotals.teamCost,
    amountDue: money.amountDue,
    tax: money.tax,
    allocated: feeTotals.allocated,
    unallocated: feeTotals.unallocated,
    paid: money.paid,
    owed: money.outstanding,
    currency: currencyCodes[0] ?? 'CAD',
    showMoney: showMoneyKpis,
    refreshing: isFetching,
  }

  /** How the header prints money, days and copy — one set for every part of it. */
  const standingFormatters: StandingFormatters = {
    t: balanceText,
    money: (minor) =>
      formatCurrency({ amount: minor, currencyCode: summaryProps.currency }),
    day: formatStandingDay,
  }

  // Sep 23 — the same rule the server's `remindableSlots()` applies, players and team
  // officials alike (balanceRowKind), so the count and the send list agree.
  const remindableSlots = useMemo(
    () =>
      registrations.flatMap((registration) =>
        registration.slots.filter(isRemindableSlot)
      ),
    [registrations]
  )
  const remindableCount = remindableSlots.length

  /**
   * The ticked rows, resolved against what is CURRENTLY on screen. Intersecting
   * with `rows` rather than trusting the id list is what keeps the counts honest
   * after a refetch: a row that was invoiced (or filtered away) since the tick
   * is no longer a row the toolbar may act on, and AG Grid's own selection
   * event can lag a payload replacement by a render.
   */
  const selectedRows = useMemo(() => {
    if (selectedSlotIds.length === 0) {
      return []
    }
    const ticked = new Set(selectedSlotIds)
    // Slot rows only: a candidate has no checkbox and nothing to invoice or remind.
    return rows.filter(isSlotTableRow).filter((row) => ticked.has(row.key))
  }, [rows, selectedSlotIds])

  // Each bulk action counts only the subset it can actually act on, so "Send 3
  // Invoices" never includes a row that is already invoiced.
  const invoiceableSelected = useMemo(
    () => selectedRows.filter(isInvoiceable),
    [selectedRows]
  )
  const remindableSelected = useMemo(
    () => selectedRows.filter(isRemindable),
    [selectedRows]
  )

  /**
   * Who a bulk reminder would reach, by name (James, Jul 31 — "they don't know
   * who they're about to remind"). Advisory only: the server recomputes the set
   * on send, which the modal's copy still says.
   */
  const reminderRecipients = useMemo(() => {
    if (notifyTarget === null || notifyTarget.slotId !== null) {
      return []
    }
    // Sep 23 — "Jeff Day · Team Rep": an official reads apart from Jeff Day the player.
    if (notifyTarget.selected) {
      return remindableSelected.map(
        (row) =>
          lineLabel(row.slot, balanceLabel('common:balanceTypeTeamRep')) ?? '—'
      )
    }
    // The unfiltered bulk audience — the same whole-payload scan remindableCount
    // does, so the list and the count can never disagree.
    return remindableSlots.map(
      (slot) =>
        lineLabel(slot, balanceLabel('common:balanceTypeTeamRep')) ?? '—'
    )
  }, [notifyTarget, remindableSelected, remindableSlots])

  // Non-null ⇒ per-slot reminder (drives the modal's copy); null ⇒ bulk. Resolved
  // from the live server payload so the name matches the current occupant.
  const reminderPlayerName = useMemo(() => {
    if (notifyTarget === null || notifyTarget.slotId === null) {
      return null
    }
    for (const registration of registrations) {
      const slot = registration.slots.find((s) => s.id === notifyTarget.slotId)
      if (slot) {
        return lineLabel(slot, balanceLabel('common:balanceTypeTeamRep')) ?? '—'
      }
    }
    return '—'
  }, [notifyTarget, registrations])

  // Always fetch the CURRENT plan before offering to settle. This is what makes
  // a 409 impossible to hit blindly: the admin confirms the fingerprint they
  // were just shown, and a second drift simply re-opens this same review.
  const openVerifyReview = (
    batchId: string,
    expectedKind: BalanceLineKind | undefined
  ) => {
    setVerifyBatchId(batchId)
    setVerifyKind(expectedKind)
    setVerifyPreview(null)
    setVerifyError(null)
    setVerifyLoading(true)

    previewVerifyOfflinePayment(
      teamId,
      selectedRosterId ?? '',
      batchId,
      expectedKind
    )
      .then(setVerifyPreview)
      .catch((error) =>
        setVerifyError(
          expectedKind === 'official'
            ? officialWriteMessage(error)
            : guardMessage(error)
        )
      )
      .finally(() => setVerifyLoading(false))
  }

  const confirmVerify = (settlementFingerprint: string) => {
    if (verifyBatchId === null) {
      return
    }
    mutations.verifyOffline
      .mutateAsync({
        batchId: verifyBatchId,
        settlementFingerprint,
        expectedKind: verifyKind,
      })
      .then(() => {
        setVerifyBatchId(null)
        setVerifyPreview(null)
      })
      .catch((error) => {
        // Sep 23 — a 409 that says this page is out of date is not a drift: re-opening
        // the review would only be refused again. Say so instead.
        if (officialWriteError(error) === 'refresh_required') {
          setVerifyError(officialWriteMessage(error))
          return
        }
        // A SECOND drift between review and confirm: re-open the review with the
        // refreshed plan rather than settling or dead-ending on an error toast.
        const status =
          (error as { status?: number; statusCode?: number })?.status ??
          (error as { statusCode?: number })?.statusCode
        if (status === 409) {
          openVerifyReview(verifyBatchId, verifyKind)
          return
        }
        setVerifyError(
          verifyKind === 'official'
            ? officialWriteMessage(error)
            : guardMessage(error)
        )
      })
  }

  // Deep link: once the authorised roster is settled, open the batch's review.
  // Fires once — re-opening on every render would fight the admin closing it.
  const [deepLinkOpened, setDeepLinkOpened] = useState(false)
  useEffect(() => {
    // Waits for the sheet itself: the link names only a batch, and the batch's own
    // `line_kind` (Sep 23) is read from the loaded sheet.
    if (
      deepLinkOpened ||
      !deepLink.verify ||
      selectedRosterId === null ||
      isLoading
    ) {
      return
    }
    setDeepLinkOpened(true)
    openVerifyReview(
      deepLink.verify,
      batchWriteKindOnSheet(registrations, deepLink.verify)
    )
    // Fires exactly once (the deepLinkOpened guard); openVerifyReview is a
    // render-scoped closure and is intentionally omitted so a re-render can't re-open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkOpened, deepLink.verify, selectedRosterId, isLoading])

  const voidingBatchId = mutations.voidOffline.isPending
    ? mutations.voidOffline.variables?.batchId ?? null
    : null

  /**
   * Voids a batch with its OWN `line_kind` (Sep 23) — the line it was recorded on,
   * never inferred from the row or history section it happens to sit in.
   */
  const voidBatch = (batch: OfflinePaymentBatch) => {
    const expectedKind = batchWriteKind(batch)
    mutations.voidOffline
      .mutateAsync({ batchId: batch.id, expectedKind })
      .catch((error) =>
        message.error(
          expectedKind === 'official'
            ? officialWriteMessage(error)
            : guardMessage(error)
        )
      )
  }

  // Zero-slot sheet: the table zone shows the set-up empty state instead, and
  // the FirstRunModal floats over it until dismissed. The empty state's CTA
  // clears the dismissal so the modal is always recoverable without a reload.
  const sheetIsEmpty =
    singleRegistration !== null && singleRegistration.slots.length === 0

  // Sep 24 (B-8) — the set-up still applies while no PLAYER line exists: a rep who
  // paid toward the deposit at registration leaves only their own line, which the
  // grid shows behind the modal. Dismissing it works exactly as before.
  const firstRunOpen =
    !firstRunDismissed &&
    singleRegistration !== null &&
    hasNoPlayerLines(singleRegistration)

  /**
   * SN-4709 (James, Jul 31) — open "Send N Invoices" and load its preview.
   *
   * The preview is fetched HERE rather than inside the modal because it is a
   * POST (the slot ids travel in the body), so there is no cache key to hang a
   * query on — and because each row must carry its own fingerprint through to
   * the confirm.
   */
  const openBatchInvoice = () => {
    const slotIds = invoiceableSelected.map((row) => row.slot.id)
    if (slotIds.length === 0) {
      return
    }
    setBatchInvoiceOpen(true)
    setBatchPreview(null)
    setBatchError(null)
    setBatchLoading(true)

    // Sep 23 — each team official's line names its kind (R2-4); players send none.
    const officialKinds: Record<string, BalanceLineKind> = {}
    for (const row of invoiceableSelected) {
      if (isOfficialLine(row.slot)) {
        officialKinds[row.slot.id] = 'official'
      }
    }

    previewLockBatch(
      teamId,
      selectedRosterId ?? '',
      slotIds,
      Object.keys(officialKinds).length > 0 ? officialKinds : undefined
    )
      .then(setBatchPreview)
      .catch((error) => setBatchError(guardMessage(error)))
      .finally(() => setBatchLoading(false))
  }

  /**
   * Best-effort by design: `failed` is a normal 200 outcome, so a slot whose
   * schedule drifted since the preview is REPORTED rather than allowed to cost
   * the others their invoices. The selection is cleared either way — the rows it
   * pointed at have changed state.
   */
  const confirmBatchInvoice = (
    locks: { slot_id: string; preview_fingerprint: string }[]
  ) => {
    mutations.lockSlotsBatch
      .mutateAsync(
        locks.map((lock) => {
          const expectedKind = slotWriteKind(lock.slot_id)
          return expectedKind ? { ...lock, expected_kind: expectedKind } : lock
        })
      )
      .then((result) => {
        setBatchInvoiceOpen(false)
        setBatchPreview(null)
        setSelectedSlotIds([])
        if (result.failed.length > 0) {
          message.warning(
            balanceText('common:balanceBatchInvoicePartial', {
              sent: result.locked.length,
              failed: result.failed.length,
            })
          )
          return
        }
        message.success(
          result.locked.length > 0
            ? balanceText('common:balanceBatchInvoiceSuccess', {
                count: result.locked.length,
              })
            : balanceText('common:balanceBatchInvoiceNone')
        )
      })
      .catch((error) => message.error(guardMessage(error)))
  }

  /** SN-8310 — undo an unpaid added invoice once the rep has confirmed it. */
  const confirmRemoveAddedInvoice = () => {
    if (!removeInvoiceTarget) {
      return
    }
    mutations.removeAddedInvoice
      .mutateAsync({
        slotId: removeInvoiceTarget.slotId,
        billUid: removeInvoiceTarget.bill.uid,
      })
      .then(() => {
        setRemoveInvoiceTarget(null)
        message.success(balanceText('common:balanceRemoveAddedInvoiceSuccess'))
      })
      .catch((error) => message.error(guardMessage(error)))
  }

  /**
   * "Split Evenly" (James, Jul 31) — the explicit replacement for the Auto-Split
   * toggle. Same endpoint the toggle's re-split used; the difference is that it
   * happens when the rep asks, once, after being told what it overwrites.
   */
  const confirmSplitEvenly = () => {
    if (!singleRegistration) {
      return
    }
    mutations.runAutoDistribute
      .mutateAsync({ team_registration_id: singleRegistration.id })
      .then(() => setSplitEvenlyOpen(false))
      .catch((error) => message.error(guardMessage(error)))
  }

  /**
   * SN-4709 (James, Aug 12) — the ONE composer's fan-out.
   *
   * Three endpoints, three result shapes, three authorization gates — all kept.
   * Only the SHELL merged ("it's just one mechanism and you can do multiple"),
   * so this dispatches whichever channels were ticked and then reports what each
   * one actually did.
   *
   * allSettled, not all: a channel that fails must not swallow the ones that
   * went out, and the composer must not close pretending nothing happened. Every
   * count reported is the SERVER's own (`queued_count`), never the client's
   * audience estimate — the two differ by opt-outs and unreachable contacts.
   *
   * None of these invalidate the balance cache: notifying nobody changes what
   * anybody owes.
   */
  const confirmNotify = (body: NotifyComposerBody) => {
    if (notifyTarget === null) {
      return
    }
    const note = body.message.trim() ? body.message.trim() : null

    const sends: Array<{ channel: string; call: Promise<unknown> }> = []

    if (body.channels.includes('reminder')) {
      /*
       * Three audiences, two endpoints. A SELECTION targets a subset, which the
       * bulk endpoint cannot express — it is server-authoritative over the whole
       * remindable set by design — so it fans out the per-slot reminder rather
       * than inventing a fourth endpoint with its own authorization and its own
       * definition of "remindable".
       */
      const reminder =
        notifyTarget.slotId !== null
          ? mutations.sendSlotReminder.mutateAsync({
              slotId: notifyTarget.slotId,
              message: note,
            })
          : notifyTarget.selected
          ? Promise.allSettled(
              remindableSelected.map((row) =>
                mutations.sendSlotReminder.mutateAsync({
                  slotId: row.slot.id,
                  message: note,
                })
              )
            ).then((results) => ({
              queued_count: results.reduce(
                (sum, result) =>
                  sum +
                  (result.status === 'fulfilled'
                    ? result.value.queued_count
                    : 0),
                0
              ),
            }))
          : mutations.sendReminders.mutateAsync(note)

      sends.push({ channel: 'reminder', call: reminder })
    }

    if (body.channels.includes('post')) {
      sends.push({
        channel: 'post',
        call: mutations.postAndNotify.mutateAsync({
          subject: body.subject,
          message: body.message,
        }),
      })
    }

    if (sends.length === 0) {
      return
    }

    Promise.allSettled(sends.map((send) => send.call)).then((results) => {
      const done: string[] = []
      const failed: string[] = []

      results.forEach((result, index) => {
        const channel = sends[index].channel
        if (result.status === 'rejected') {
          failed.push(channel)

          return
        }
        const value = result.value as {
          queued_count?: number
          enqueue_failed?: boolean
          unreachable_count?: number
        }
        const count = value.queued_count ?? 0

        if (channel === 'reminder') {
          done.push(
            count > 0
              ? balanceText('common:balanceNotifyResultReminder', { count })
              : balanceText('common:balanceReminderNone')
          )
        } else {
          done.push(balanceText('common:balanceNotifyResultPost', { count }))
          // The post committed; if a fan-out chunk failed to enqueue, say so
          // honestly rather than implying every member was notified.
          if (value.enqueue_failed) {
            message.warning(balanceText('common:balancePostEnqueueWarning'))
          }
        }
      })

      if (done.length > 0) {
        message.success(done.join(' · '))
      }
      // A partial failure keeps the composer OPEN with what was typed, so the
      // remaining channel can be retried without recomposing the message.
      if (failed.length > 0) {
        // The first rejection's own reason, narrowed by the discriminant rather
        // than cast — `PromiseRejectedResult` is a lib type this eslint config
        // does not know, and the loop reads the same either way.
        let reason: unknown
        for (const result of results) {
          if (result.status === 'rejected') {
            reason = result.reason
            break
          }
        }
        message.error(guardMessage(reason))

        return
      }
      setNotifyTarget(null)
    })
  }

  // Discovery must resolve before a roster exists to fetch a sheet for; without
  // this the tab flashes an empty sheet on first paint.
  if (isLoading || isLoadingRosters) {
    return <LoadingSpinner size={4} />
  }

  if (isError) {
    // 403 never lands here (the hook resolves it to []); real errors get a retry.
    return (
      <ConfigProvider theme={BALANCE_THEME}>
        <Alert
          type='error'
          showIcon
          message={balanceText('common:balanceLoadError')}
          action={
            <Button size='small' onClick={() => refetch()}>
              {balanceLabel('common:balanceRetry')}
            </Button>
          }
        />
      </ConfigProvider>
    )
  }

  /*
   * The shared Season picker named a season this caller has no Team Budget for.
   * Said plainly — an empty grid would read as "this team owes nothing".
   */
  if (isSeasonControlled && selectedRosterId === null) {
    return (
      <div css={seasonEmptyStyle}>
        <EmptyStateBox
          icon={<WalletOutlined />}
          title={balanceText('common:balanceSeasonEmptyTitle')}
          subtitle={balanceText('common:balanceSeasonEmptyBody')}
        />
      </div>
    )
  }

  /*
   * A pinned scope that resolved to nothing — the roster is not in discovery
   * (permissions, or the registration moved), or the sheet came back without
   * this registration. Said plainly, because the alternative renderings are both
   * wrong: an empty grid reads as "this team owes nothing", and un-pinning would
   * show a different roster's money under this registration's heading.
   */
  if (scope && singleRegistration === null) {
    return (
      <ConfigProvider theme={BALANCE_THEME}>
        <Alert
          type='info'
          showIcon
          data-testid='balance-scope-unavailable'
          message={balanceText('common:balanceScopeUnavailable')}
        />
      </ConfigProvider>
    )
  }

  return (
    <ConfigProvider theme={BALANCE_THEME}>
      <div
        ref={contentRef}
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px 0;
        `}
      >
        {/*
          G4 (Sep 1) — the sheet is a COMMAND CENTER, not a table: slot creation,
          splitting, invoicing, cash recording and notifications all act on the
          team's money. An admin gets every one of them (Dennis's idea, Jeff: "I
          love that actually"), so the one thing the surface owes them is a plain
          statement of whose money they are about to move.
        */}
        {scope ? (
          <Alert
            type='info'
            showIcon
            data-testid='balance-admin-scope-note'
            message={balanceText('common:balanceAdminScopeNote')}
          />
        ) : null}

        {hiddenFinderListings.map(({ registration, count, cause }) => (
          <Alert
            key={`finder-hidden-${registration.id}`}
            type='warning'
            showIcon
            data-testid={`balance-finder-hidden-${registration.id}`}
            data-cause={cause}
            message={
              // Named on All-Divisions, where "looking for players is off"
              // would not say which competition's switch is meant.
              visibleRegistrations.length > 1
                ? `${registration.division.name} · ${balanceText(
                    FINDER_HIDDEN_NOTICE_KEYS[cause],
                    { count }
                  )}`
                : balanceText(FINDER_HIDDEN_NOTICE_KEYS[cause], { count })
            }
            action={
              // Only where the settings can help: they change the switch, and
              // list the server's reasons when the sheet cannot name one. A
              // competition's Player Finder or a full roster is not set there,
              // and privacy is this toolbar's own Private Team switch.
              onOpenPlayerFinder &&
              (cause === 'recruiting_off' || cause === 'other') ? (
                <Button
                  size='small'
                  onClick={() => onOpenPlayerFinder(registration.id)}
                  data-testid='balance-finder-open-settings'
                >
                  {balanceLabel('common:balanceFinderOpenSettings')}
                </Button>
              ) : undefined
            }
          />
        ))}

        {!showMoneyKpis && visibleRegistrations.length > 0 ? (
          <Alert
            type='info'
            showIcon
            message={balanceText('common:balanceMixedCurrency')}
            data-testid='mixed-currency-note'
          />
        ) : null}

        {/*
          SN-4709 (Inon, Aug 12) — ONE header panel.

          Everything this page says about the roster you just picked, inside one
          border and in reading order: WHICH roster and its status, WHERE the
          money is IN TIME, then HOW MUCH. It replaces an uncarded selector with
          a pill floating beside it, a timeline card starting at 45% of the row
          and two more cards below split at 49% — four things at three rhythms,
          which is what read as "quite unorganized".
        */}
        {rosterPicker}

        <BalanceHeaderPanel
          /*
            SN-8310 (Jeff and James, Sep 22; C4 over C2) — WHERE THE TEAM STANDS,
            in a sentence, beside its pill and the status legend. All Divisions
            keeps its list of named pills: divisions have different dates, so
            there is no single sentence or timeline to give (Sep 22 pick).
            Without a status there is no banner: all it could say was the
            outstanding and total the figures row shows (Inon, Sep 24).
          */
          header={
            singleRegistration ? (
              standing ? (
                <TeamStandingHeader
                  standing={standing}
                  status={singleRegistration.payment_status}
                  money={money}
                  league={singleRegistration.division.root_name}
                  fmt={standingFormatters}
                />
              ) : undefined
            ) : (
              statusBadges
            )
          }
          /*
            WHEN: the league's three dates as milestones, each with the money
            still in play on it, and Today between them. Only with a standing —
            without a status there are no dates to show.
          */
          timeline={
            standing ? (
              <PaymentMilestones
                standing={standing}
                money={money}
                fmt={standingFormatters}
                now={now}
              />
            ) : undefined
          }
          /*
            HOW MUCH, from the server, side by side. Shown whatever the grid's
            filters leave on screen — they no longer change the team's money.
            One registration drops the card's bar and title (the milestones
            already show progress); All Divisions keeps them.
          */
          moneyMeter={
            visibleRegistrations.length > 0 ? (
              <BalanceSummaryCards
                embedded
                figuresOnly={singleRegistration !== null}
                {...summaryProps}
              />
            ) : undefined
          }
        />

        <BalanceControls
          registrations={registrations}
          // Pinned scope hides the division filter for the same reason it hides
          // the roster switcher: neither is a choice on this surface.
          hideDivisionFilter={Boolean(scope)}
          rowFilter={rowFilter}
          onRowFilter={setRowFilter}
          showSheetActions={singleRegistration !== null}
          sheetActionsPending={mutations.runAutoDistribute.isPending}
          onSplitEvenly={() => setSplitEvenlyOpen(true)}
          evenSplitLabel={evenSplitLabel}
          selectedCount={invoiceableSelected.length}
          onSendInvoices={openBatchInvoice}
          selectedRegistrationId={selectedRegistrationId}
          onSelectRegistration={(id) => {
            setSelectedRegistrationId(id)
            setFirstRunDismissed(false)
          }}
          showCancelled={showCancelled}
          onToggleCancelled={setShowCancelled}
          cancelledToggle={cancelledToggle}
          addDisabled={
            singleRegistration === null ||
            mutations.createSlots.isPending ||
            // F10: a header-locked sheet is frozen — Add Line Item must be disabled too (release and
            // delete already gate on the lock), so the affordance matches the server's rejection.
            singleRegistration.locked
          }
          /*
           * D46 — Add Player Slot used to create one slot instantly. The Player
           * Finder opt-in has to be ASKED per slot (never inferred), so it now
           * opens AddPlayerSlotModal instead.
           */
          onAddLineItem={() => {
            if (!singleRegistration) {
              return
            }
            setAddSlotOpen(true)
          }}
          /*
           * SN-4709 (James, Aug 12) — the audience IS the selection, with the
           * whole-roster fallback preserved. James on needing to select first:
           * "you just have to select first. That's fine." So there is one
           * button, and what it targets follows what is ticked.
           */
          onNotify={() =>
            setNotifyTarget({
              slotId: null,
              selected: remindableSelected.length > 0,
            })
          }
          /*
           * SN-4709 Team Privacy (Jeff, Sep 1).
           *
           * Single-registration only, by construction: privacy and the share link belong
           * to ONE registration, so on All-Divisions both are null and the controls are
           * simply absent — the same rule `showSheetActions` already applies.
           *
           * `?? false` rather than `?? null`: an API that predates the split omits the key
           * entirely, and a team with no answer IS public (D8), so the control still shows
           * the truth. `share_link` stays nullable — a missing link means there is nothing
           * to copy, and a disabled copy button would be worse than none.
           */
          isPrivate={
            singleRegistration ? singleRegistration.is_private ?? false : null
          }
          shareLink={singleRegistration?.share_link ?? null}
          onTogglePrivacy={(value) => {
            if (!singleRegistration) {
              return
            }
            // mutateAsync + an explicit catch, matching every other mutation on this
            // screen: the hook invalidates on success, and a rejection has to surface as a
            // message rather than an unhandled promise.
            mutations.updateSettings
              .mutateAsync({
                team_registration_id: singleRegistration.id,
                is_private: value,
              })
              .catch((error: unknown) => message.error(guardMessage(error)))
          }}
        />

        {sheetIsEmpty ? (
          <BalanceEmptyState onSetUp={() => setFirstRunDismissed(false)} />
        ) : (
          <Suspense fallback={<LoadingSpinner size={4} />}>
            <BalanceGrid
              rows={rows}
              loading={mutations.removeSlot.isPending}
              pendingSlotIds={pendingSlotIds}
              pendingAmounts={pendingAmounts}
              onSelectionChange={setSelectedSlotIds}
              evenSplit={evenSplit}
              onViewPayments={setPaymentsSlotId}
              onAmountSubmit={(slotId, amount) => {
                // Sep 23 — a team official's line is previewed and confirmed first;
                // the flow reports its own refusals.
                const target = resolveSlotTarget(registrations, slotId)
                if (target && isOfficialLine(target.slot)) {
                  return officialFlows.submitAmount(
                    slotId,
                    lineName(target.slot) ?? '',
                    amount,
                    target.registration.currency?.code ?? 'CAD'
                  )
                }
                return mutations.updateSlotAmount.mutateAsync({
                  slotId,
                  amount,
                })
              }}
              onCandidateAmountSubmit={officialFlows.submitCandidate}
              // D5 — the same condition that mounts the official confirmation below:
              // while it owns focus the grid leaves focus alone, and when it closes
              // the grid puts focus back on the person, wherever their row moved.
              focusPaused={officialFlows.state !== null}
              onDeleteSlot={(slotId) =>
                runSlotMutation(mutations.removeSlot, slotId)
              }
              onLockSlot={(slotId) => setLockTargetSlotId(slotId)}
              onUnlockSlot={(slotId) =>
                mutations.unlockSlot
                  .mutateAsync({ slotId, expectedKind: slotWriteKind(slotId) })
                  .catch((error) =>
                    message.error(writeFailureMessage(slotId, error))
                  )
              }
              onAddInvoice={(slotId) => setAddInvoiceSlotId(slotId)}
              onReleaseSlot={(slotId) =>
                runSlotMutation(mutations.releaseSlot, slotId)
              }
              onRecordOffline={(slotId) => setOfflineTargetSlotId(slotId)}
              onRemindSlot={(slotId) => setNotifyTarget({ slotId })}
              // Edit Player is offered ONLY when the caller can manage the roster
              // AND its players loaded (view-gated — a 403 leaves it off). Passing
              // undefined omits the kebab action entirely (server stays authoritative).
              onEditPlayer={
                canManageRoster && rosterPlayersQuery.isSuccess
                  ? openPlayerEditor
                  : undefined
              }
              // SN-8357 — which occupants the action can actually open for. The
              // click guard in openPlayerEditor stays as defence in depth.
              rosterPlayerIds={rosterPlayerIds}
              // D46 — "List in Player Finder…" opens the position picker; the
              // kebab itself decides visibility from can_list_in_player_finder.
              onListInFinder={(slotId) =>
                setFinderTarget({ slotId, mode: 'list' })
              }
              // A listed slot's position — a position edit, never an opt-in.
              onEditFinderPosition={(slotId) =>
                setFinderTarget({ slotId, mode: 'edit-position' })
              }
              // Untick is one call, never gated — see balanceGridActions.
              onRemoveFromFinder={(slotId) =>
                mutations.updateSlotFinderListing
                  .mutateAsync({ slotId, listed: false })
                  .catch((error) => message.error(guardMessage(error)))
              }
            />
          </Suspense>
        )}

        <OfflineHistorySection
          registrations={visibleRegistrations}
          onVoidOffline={voidBatch}
          voidingBatchId={voidingBatchId}
        />

        <FirstRunModal
          open={firstRunOpen}
          submitting={mutations.createSlots.isPending}
          onSubmit={(count) => {
            if (!singleRegistration) {
              return Promise.resolve()
            }
            return mutations.createSlots.mutateAsync({
              team_registration_id: singleRegistration.id,
              count,
            })
          }}
          onClose={() => setFirstRunDismissed(true)}
        />

        {/*
          SN-4709 × SN-8081 (D46) — one modal, three modes. `create` is Add Player
          Slot (count + opt-in + position); `list` is the row kebab's "List in
          Player Finder…" and `edit-position` its "Edit Player Finder Position",
          where the slot already exists and only the position is asked for.

          Both slot modes list the slot with a position, so each says which one
          the rep chose (TF-02): only List may turn the team's recruiting back
          on, and an Edit Position whose slot was unlisted meanwhile is refused
          (409) rather than quietly listing it again.
        */}
        <AddPlayerSlotModal
          open={addSlotOpen || finderTarget !== null}
          mode={finderTarget?.mode ?? 'create'}
          submitting={
            mutations.createSlots.isPending ||
            mutations.updateSlotFinderListing.isPending
          }
          canListInPlayerFinder={
            singleRegistration?.can_list_in_player_finder ?? false
          }
          recruitingOff={
            (finderTargetSlot?.registration ?? singleRegistration)
              ?.player_finder_recruiting === false
          }
          initialPlayerTypeId={
            finderTargetSlot?.slot.finder_position?.id ?? null
          }
          sportId={sportId}
          onSubmit={({ count, finderListed, finderPlayerTypeId }) => {
            if (finderTarget !== null) {
              return mutations.updateSlotFinderListing
                .mutateAsync({
                  slotId: finderTarget.slotId,
                  listed: true,
                  action:
                    finderTarget.mode === 'edit-position'
                      ? 'edit_position'
                      : 'list',
                  playerTypeId: finderPlayerTypeId,
                })
                .then(() => setFinderTarget(null))
                .catch((error) => {
                  // 409: the slot (or its registration) changed since this
                  // opened — nothing was saved and the sheet is refetching.
                  // Close rather than offer the same save again: the rep picks
                  // the action again from the refreshed row, so listing it
                  // takes a fresh List with its own notice. The modal shows why.
                  if ((error as { status?: number } | null)?.status === 409) {
                    setFinderTarget(null)
                  }
                  throw error
                })
            }

            if (!singleRegistration) {
              return Promise.resolve()
            }

            return mutations.createSlots
              .mutateAsync({
                team_registration_id: singleRegistration.id,
                count,
                finder_listed: finderListed,
                finder_player_type_id: finderPlayerTypeId,
              })
              .then(() => setAddSlotOpen(false))
          }}
          onClose={() => {
            setAddSlotOpen(false)
            setFinderTarget(null)
          }}
        />

        <Drawer
          open={paymentsTarget !== null}
          onClose={() => setPaymentsSlotId(null)}
          /**
           * 480 was set when this drawer held a billing table only. An offline
           * batch line now carries amount + source · date + a status tag + up to
           * three actions, and at 480 there is simply not enough width — the tag
           * collided with the buttons, and once that was fixed the date truncated
           * to "Cash · …". Widening is the honest fix; squeezing the content is
           * how you end up hiding the date on a money record.
           */
          width={620}
          /**
           * U2 — antd defaults a Drawer to z-index 1000, which is UNDER the app's
           * fixed header (`zIndex.header`, 1020). The drawer's header strip occupies
           * y 0–57px, so the nav painted straight over the title AND over the close
           * (X) button: `document.elementFromPoint` at the title's centre returned
           * the app header, and the X was unclickable. Named scale value, never a
           * hardcoded number — `overlay` is the tier above the nav and below modals,
           * which is exactly what a drawer is.
           *
           * SN-8449 — inside a host modal that tier is far UNDER the host, so the
           * drawer goes one layer above it instead: still below the WarningModal
           * (10011) and the Verify re-review it opens itself.
           */
          zIndex={hostZIndex === undefined ? zIndex.overlay : hostZIndex + 1}
          /*
            Inon, Aug 12 — "put lower please, like the one you had before". The
            drawer starts at y=0 (it sits ABOVE the app nav, see zIndex below),
            so its header was flush against the top of the window with the title
            and the close button jammed into the very edge. This is the breathing
            room the old layout got for free from the nav band above it.
          */
          /*
            The PANEL stays full height — it is the page's right-hand surface and
            a drawer that starts a third of the way down reads as a floating box
            (Inon, Aug 12: "the background should take up the full page
            vertically as well, just reposition the actual contents").
            Only the CONTENTS drop, to where this tab's own content begins, so
            the title lines up with the panel behind it instead of sitting in the
            browser chrome.
          */
          styles={{
            content: { paddingTop: drawerTop },
            header: { paddingTop: 18, paddingBottom: 18 },
          }}
          /* antd's default close glyph is 16px in a small hit box, which on a
             620px drawer reads as an afterthought. This is the only way out of
             the drawer, so it gets a target worth aiming at. */
          closeIcon={
            <CloseOutlined
              css={css`
                font-size: 22px;
                padding: 4px;
                color: ${colors.NEUTRAL_30};
                transition: color 0.15s ease;
                &:hover {
                  color: ${colors.NEUTRAL_0};
                }
              `}
            />
          }
          destroyOnClose
          title={
            paymentsTarget
              ? balanceText('common:balancePaymentsFor', {
                  // Sep 23 — "Jeff Day · Team Rep" on an official's line.
                  name:
                    lineLabel(
                      paymentsTarget.slot,
                      balanceLabel('common:balanceTypeTeamRep')
                    ) ?? balanceLabel('common:balanceOpenSlot'),
                })
              : ''
          }
        >
          {paymentsTarget ? (
            <SlotExpandedRow
              billingSchedules={paymentsTarget.slot.billing_schedules}
              currencyCode={paymentsTarget.registration.currency?.code ?? 'CAD'}
              offlinePayments={paymentsTarget.slot.offline_payments}
              onVoidOffline={voidBatch}
              voidingBatchId={voidingBatchId}
              onVerifyOffline={(batch) =>
                openVerifyReview(batch.id, batchWriteKind(batch))
              }
              verifyingBatchId={verifyingBatchId}
              onRemoveAddedInvoice={(bill) => {
                const currencyCode =
                  paymentsTarget.registration.currency?.code ?? 'CAD'
                // Sep 23 — undoing an official's added invoice gives money back to
                // player shares, so it previews that effect before it asks.
                if (isOfficialLine(paymentsTarget.slot)) {
                  officialFlows.startRemoval(
                    paymentsTarget.slot.id,
                    lineName(paymentsTarget.slot) ?? '',
                    bill,
                    currencyCode
                  )
                  return
                }
                setRemoveInvoiceTarget({
                  slotId: paymentsTarget.slot.id,
                  bill,
                  currencyCode,
                })
              }}
              removingBillUid={
                mutations.removeAddedInvoice.isPending
                  ? mutations.removeAddedInvoice.variables?.billUid ?? null
                  : null
              }
              removeBlockedReason={
                isViewerRestricted(paymentsTarget.slot)
                  ? balanceText('common:balanceOfficialOwnLineTip')
                  : undefined
              }
              /**
               * A2 — the occupant comes from the LIVE server payload, and
               * SlotExpandedRow keys the editor by it: if a refetch shows the slot
               * reassigned while the drawer is open, the editor remounts and an
               * unsaved draft about the previous player is discarded rather than
               * being carried over onto the new one. The id the editor captured at
               * mount is what it sends, so the server's 409 only ever has to catch
               * a change we have not refetched yet.
               */
              slotId={paymentsTarget.slot.id}
              playerRegistrationId={paymentsTarget.slot.player_registration_id}
              note={paymentsTarget.slot.note ?? null}
              savingNote={mutations.updateSlotNote.isPending}
              onSaveNote={(note, expectedPlayerRegistrationId) =>
                mutations.updateSlotNote.mutateAsync({
                  slotId: paymentsTarget.slot.id,
                  note,
                  expectedPlayerRegistrationId,
                })
              }
            />
          ) : null}
        </Drawer>

        <VerifyReviewModal
          open={verifyBatchId !== null}
          loading={verifyLoading}
          preview={verifyPreview}
          error={verifyError}
          currencyCode={currencyCodes[0] ?? 'CAD'}
          confirming={mutations.verifyOffline.isPending}
          onConfirm={confirmVerify}
          onClose={() => {
            setVerifyBatchId(null)
            setVerifyPreview(null)
            setVerifyError(null)
          }}
        />

        <LockConfirmModal
          rosterId={selectedRosterId ?? ''}
          open={lockTarget !== null}
          teamId={teamId}
          slot={lockTarget?.slot ?? null}
          registration={lockTarget?.registration ?? null}
          submitting={mutations.lockSlot.isPending}
          onConfirm={(previewFingerprint) => {
            if (!lockTarget) {
              return Promise.resolve()
            }
            return mutations.lockSlot
              .mutateAsync({
                slotId: lockTarget.slot.id,
                previewFingerprint,
                expectedKind: writeKindFor(lockTarget.slot),
              })
              .then(() => setLockTargetSlotId(null))
          }}
          onClose={() => setLockTargetSlotId(null)}
          // Sep 23 — a team official's line is one charge due today, paid by them.
          expectedKind={lockTarget ? writeKindFor(lockTarget.slot) : undefined}
          payerName={
            lockTarget && isOfficialLine(lockTarget.slot)
              ? lineName(lockTarget.slot)
              : null
          }
        />

        <AddInvoiceModal
          rosterId={selectedRosterId ?? ''}
          open={addInvoiceTarget !== null}
          teamId={teamId}
          slot={addInvoiceTarget?.slot ?? null}
          registration={addInvoiceTarget?.registration ?? null}
          submitting={mutations.addInvoice.isPending}
          onConfirm={(body) => {
            if (!addInvoiceTarget) {
              return Promise.resolve()
            }
            const name = lineName(addInvoiceTarget.slot) ?? ''
            return mutations.addInvoice
              .mutateAsync({ slotId: addInvoiceTarget.slot.id, body })
              .then(() => {
                setAddInvoiceSlotId(null)
                message.success(
                  balanceText('common:balanceAddInvoiceSuccess', { name })
                )
              })
          }}
          onClose={() => setAddInvoiceSlotId(null)}
          // Sep 23 (T6) — on an official's line the extra comes out of the players.
          expectedKind={
            addInvoiceTarget ? writeKindFor(addInvoiceTarget.slot) : undefined
          }
        />

        {/* SN-8310 — undoing an added invoice changes what the player owes, so it
            asks first. Opened from the payments drawer; WarningModal stacks above it. */}
        <WarningModal
          isOpen={removeInvoiceTarget !== null}
          onClose={() => setRemoveInvoiceTarget(null)}
          onConfirm={confirmRemoveAddedInvoice}
          title={balanceLabel('common:balanceRemoveAddedInvoiceTitle')}
          description={
            removeInvoiceTarget
              ? balanceText('common:balanceRemoveAddedInvoiceBody', {
                  amount: formatCurrency({
                    amount: dollarsToMinor(removeInvoiceTarget.bill.total),
                    currencyCode: removeInvoiceTarget.currencyCode,
                  }),
                  subtotal: formatCurrency({
                    amount: dollarsToMinor(removeInvoiceTarget.bill.subtotal),
                    currencyCode: removeInvoiceTarget.currencyCode,
                  }),
                })
              : ''
          }
          cancelLabel={balanceText('common:balanceCommsCancel').toUpperCase()}
          confirmLabel={balanceText(
            'common:balanceRemoveAddedInvoiceConfirm'
          ).toUpperCase()}
          isLoading={mutations.removeAddedInvoice.isPending}
          destructive
        />

        {/*
          Sep 23 — a change to a team official's line that moves player shares asks
          first, listing every share's before and after (money rules §4). Opened by
          the Player Share of an official's line or candidate, and by an official's
          added-invoice Remove, which is the destructive variant.
        */}
        {officialFlows.state ? (
          <WarningModal
            isOpen
            onClose={officialFlows.close}
            onConfirm={officialFlows.confirm}
            title={officialEffectCopy(officialFlows.state.target).title}
            cancelLabel={balanceText('common:balanceCommsCancel').toUpperCase()}
            confirmLabel={
              officialEffectCopy(officialFlows.state.target).confirmLabel
            }
            isLoading={officialFlows.confirming}
            destructive={officialFlows.state.target.kind === 'removal'}
          >
            <OfficialEffectList
              intro={officialEffectCopy(officialFlows.state.target).intro}
              effect={officialFlows.state.preview.player_pool_effect}
              currencyCode={officialFlows.state.target.currencyCode}
              changed={officialFlows.state.changed}
            />
          </WarningModal>
        ) : null}

        {/*
          SN-4709 (James, Jul 31) — the bulk counterpart of LockConfirmModal. It
          lists every player about to be invoiced, with the SERVER's own schedule
          per slot, so a bulk money action is still read before it is confirmed.
        */}
        <BatchInvoiceModal
          open={batchInvoiceOpen}
          loading={batchLoading}
          preview={batchPreview}
          error={batchError}
          currencyCode={currencyCodes[0] ?? 'CAD'}
          submitting={mutations.lockSlotsBatch.isPending}
          onConfirm={confirmBatchInvoice}
          onClose={() => {
            setBatchInvoiceOpen(false)
            setBatchPreview(null)
            setBatchError(null)
          }}
        />

        {/*
          "Split Evenly" overwrites every uninvoiced share, so it asks first —
          which is exactly what the Auto-Split toggle it replaces never did.
        */}
        <BaseModal
          isOpen={splitEvenlyOpen}
          /* Inon, Aug 12 — the figure this button is about to write, in the
             title. Confirming "Split Evenly" without it means trusting the
             toolbar line you stopped looking at two clicks ago. Same
             `evenSplitLabel` the toolbar states, so the two cannot disagree. */
          title={
            evenSplitLabel
              ? /* balanceText, not balanceLabel: the Title Case helper is for
                   short labels and turns the trailing phrase into "Per Player".
                   This string carries its own casing. */
                balanceText('common:balanceSplitEvenlyTitleAmount', {
                  amount: evenSplitLabel,
                })
              : balanceLabel('common:balanceSplitEvenlyTitle')
          }
          okText={balanceLabel('common:balanceSplitEvenly')}
          cancelText={balanceLabel('common:balanceCommsCancel')}
          isLoading={mutations.runAutoDistribute.isPending}
          onOk={confirmSplitEvenly}
          onClose={() => setSplitEvenlyOpen(false)}
          desktopWidth='480px'
          destroyOnClose
        >
          <p data-testid='split-evenly-body'>
            {balanceText('common:balanceSplitEvenlyBody')}
          </p>
        </BaseModal>

        <OfflinePaymentModal
          rosterId={selectedRosterId ?? ''}
          open={offlineTarget !== null}
          teamId={teamId}
          slot={offlineTarget?.slot ?? null}
          currencyCode={offlineTarget?.registration.currency?.code ?? 'CAD'}
          submitting={mutations.recordOffline.isPending}
          onConfirm={(body) =>
            mutations.recordOffline.mutateAsync({
              slotId: offlineTarget?.slot.id ?? '',
              body,
            })
          }
          onClose={() => setOfflineTargetSlotId(null)}
          expectedKind={
            offlineTarget ? writeKindFor(offlineTarget.slot) : undefined
          }
          ownLine={offlineTarget?.slot.official?.viewer_is_payer === true}
        />

        {/*
          SN-4709 (James, Aug 12) — one composer for all three channels. The
          three modals it replaces asked the same two questions (who, and what do
          you want to say) and differed only in which menu item you arrived
          through.
        */}
        <NotifyComposerModal
          open={notifyTarget !== null}
          playerName={reminderPlayerName}
          count={
            notifyTarget?.selected ? remindableSelected.length : remindableCount
          }
          recipients={reminderRecipients}
          selectedOnly={notifyTarget?.selected ?? false}
          canPost={canPost}
          /*
           * Whether the REMINDER channel has anyone to reach. A per-slot target
           * is only offered from a remindable row in the first place; the bulk
           * audience is the ticked rows when there are any, else everyone who
           * owes an invoiced balance.
           */
          canRemind={
            notifyTarget?.slotId !== null ||
            (notifyTarget?.selected
              ? remindableSelected.length > 0
              : remindableCount > 0)
          }
          submitting={
            mutations.sendReminders.isPending ||
            mutations.sendSlotReminder.isPending ||
            mutations.postAndNotify.isPending
          }
          onConfirm={confirmNotify}
          onClose={() => setNotifyTarget(null)}
        />

        {/*
          SN-4709 follow-up — the full roster Edit Player editor, opened from the
          kebab. Scoped to the CURRENTLY SELECTED balance roster, and prefilled from
          the roster row (no second fetch). On Save the server owns the write; we
          refetch the sheet (position/number may have changed) and the roster rows.
        */}
        {editPlayerRow ? (
          <RosterPlayerModal
            isOpen={editPlayerRow !== null}
            editPlayer={editPlayerRow}
            teamId={teamId}
            rosterId={selectedRosterId ?? ''}
            sportId={sportId}
            sportName={sportName}
            hasAdminPermission={canManageRoster}
            hostZIndex={hostZIndex}
            onClose={() => setEditPlayerId(null)}
            onSuccess={() => {
              setEditPlayerId(null)
              refetch()
              rosterPlayersQuery.refetch()
            }}
          />
        ) : null}
      </div>
    </ConfigProvider>
  )
}

export default BalanceTab
